/**
 * @file   user-prefs.js
 * @author Ryan Saweczko
 * @date Nov 2024
 *
 * Generic server-side storage preferences via JNU
 */
'use strict';


var p$db;
var backingStores = {};

/** Return singleton connection */
function getDbConnection()
{
  if (p$db)
    return p$db;

  const jnu = require('dcp/dcp-client/jnu');
  p$db = jnu.connect('portal');
  return p$db;
}

/**
 * Load a backing store into memory from the server's database
 * 
 * @param {string} keyName - name of backing store to load
 * @returns {Promise<object>} object following the getItem, setItem, and removeItem API that localstorage uses 
 */
exports.load = async function load(keyName)
{
  var serverData;
  const db = await getDbConnection();

  const storeRows = await db.select({
    table: 'userPreferences',
    prototype: {
      keyName,
    }
  });
  if (storeRows.rowCount === 0)
    serverData = {};
  else
  {
    const prefStore = storeRows.rows[0];
    if (prefStore.storageType !== 'kvin')
      throw new Error(`Storage type was not kvin, was ${prefStore.storageType}`);
    serverData = prefStore.store;
  }
  
  backingStores[keyName] = {};
  const storageAccessor = {
    getItem: (key) => {
      if (backingStores[keyName].hasOwnProperty(key))
        return backingStores[keyName][key];
      return serverData[key];
    },
    setItem: (key, value) => {
      backingStores[keyName][key] = value;
    },
    removeItem: (key) => {
      delete serverData[key];
      delete backingStores[keyName][key];
    },
  };
  return storageAccessor;
}

/**
 * Save a backing store on the server.
 * Will fetch the store, merge it with any changes that happened locally, then write it to the db.
 * 
 * @param {string} keyName - name of backing store to save
 */
exports.save = async function save(keyName)
{
  const db = await getDbConnection();

  const storeRows = await db.select({
    table: 'userPreferences',
    prototype: {
      keyName,
    }
  });
  if (storeRows.rowCount === 0)
  {
    await db.add({
      table: 'userPreferences',
      prototype: {
        keyName,
        store: backingStores[keyName],
        storageType: 'kvin',
      }
    });
  }
  else
  {
    const prefStore = storeRows.rows[0];
    if (prefStore.storageType !== 'kvin')
      throw new Error(`Storage type was not kvin, was ${prefStore.storageType}`);

    const updatedStore = Object.assign({}, prefStore.store, backingStores[keyName]);
    const update = await db.change({
      table: 'userPreferences',
      original: prefStore,
      prototype: { store: updatedStore, }
    });
    if (update.rowCount !== 1)
      throw new Error('Backing store changed, unable to save preferences');
  }
}